import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useT } from '@/i18n';

const PaymentSuccessPage = () => {
  const t = useT();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stripe/checkout-status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setPaymentData(response.data);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #0c2431 0%, #194a50 50%, #10555e 100%)'
      }}
      data-testid="payment-success-page"
    >
      <Card className="w-full max-w-md shadow-2xl border-0" data-testid="payment-result-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#194a50] animate-spin" />
              <h2 className="text-2xl font-bold text-[#0c2431] font-['Outfit'] mb-2">
                {t('payment_success.processing_title')}
              </h2>
              <p className="text-[#194a50]/70">
                {t('payment_success.processing_subtitle')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-[#0c2431] font-['Outfit'] mb-2">
                {t('payment_success.success_title')}
              </h2>
              <p className="text-[#194a50]/70 mb-6">
                {t('payment_success.success_subtitle')}
              </p>
              {paymentData && (
                <div className="bg-[#e1dad2]/30 rounded-lg p-4 mb-6 text-left">
                  <div className="flex justify-between mb-2">
                    <span className="text-[#194a50]/70">{t('payment_success.amount')}</span>
                    <span className="font-medium text-[#0c2431]">
                      €{paymentData.amount_total ? (paymentData.amount_total / 100).toFixed(2) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#194a50]/70">{t('payment_success.status')}</span>
                    <span className="font-medium text-green-600">{t('payment_success.paid')}</span>
                  </div>
                  {paymentData.booking_id && (
                    <div className="flex justify-between mt-2">
                      <span className="text-[#194a50]/70">{t('payment_success.booking')}</span>
                      <Link to={`/bookings/${paymentData.booking_id}`} className="font-medium text-[#194a50] hover:underline">
                        {t('payment_success.view_details')}
                      </Link>
                    </div>
                  )}
                </div>
              )}
              <Link to="/bookings">
                <Button className="w-full bg-[#194a50] hover:bg-[#10555e] rounded-full" data-testid="back-to-bookings-btn">
                  {t('payment_success.back_to_bookings')}
                </Button>
              </Link>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-amber-500" />
              </div>
              <h2 className="text-2xl font-bold text-[#0c2431] font-['Outfit'] mb-2">
                {t('payment_success.expired_title')}
              </h2>
              <p className="text-[#194a50]/70 mb-6">
                {t('payment_success.expired_subtitle')}
              </p>
              <Link to="/bookings">
                <Button className="w-full bg-[#194a50] hover:bg-[#10555e] rounded-full">
                  {t('payment_success.back_to_bookings')}
                </Button>
              </Link>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-[#0c2431] font-['Outfit'] mb-2">
                {t('payment_success.error_title')}
              </h2>
              <p className="text-[#194a50]/70 mb-6">
                {t('payment_success.error_subtitle')}
              </p>
              <Link to="/bookings">
                <Button className="w-full bg-[#194a50] hover:bg-[#10555e] rounded-full">
                  {t('payment_success.back_to_bookings')}
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
